import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useReactiveVar } from '@apollo/client';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { shoppingCartOffersVar } from '../../apollo/reactive-variables/shopping-cart';
import GridEditDateCell from './GridEditDateCell';
import { UNTIL_FIFTEEN_DAYS } from '../../constants';
import dayjs from '../../config/dayjs';
import useGetCountryFromUrl from '../../hooks/useGetCountryFromUrl';
import {
  getDebtorAveragePaymentDays,
  getEffectiveMaxDate,
} from '../../helpers/date-to-pay-restrictions';
import { SECONDARY_ICON_CLASS_NAME } from '../../theme/constants/icon';

const DateToPayEditCellOnboarding = ({ params, onDateChange }) => {
  const currentOffers = useReactiveVar(shoppingCartOffersVar);
  const country = useGetCountryFromUrl();

  const dateValue = useMemo(() => {
    const selectedOffer = currentOffers.find(
      ({ invoiceId }) => params.id === invoiceId,
    );
    return selectedOffer?.dateToPay || null;
  }, [currentOffers, params.id]);

  const onChange = (row, newValue) => {
    onDateChange();
    shoppingCartOffersVar([
      { invoiceId: row.id, dateToPay: newValue },
      ...currentOffers.filter((offer) => offer.invoiceId !== row.id),
    ]);
  };

  const debtorAveragePaymentDays = useMemo(
    () => getDebtorAveragePaymentDays(
      params?.row?.receiver?.invoicesActualPaymentTerm,
      params?.row?.dateIssued,
    ),
    [params.row],
  );

  const maxDate = useMemo(() => getEffectiveMaxDate(params.row, country), [params.row, country]);

  const GridDateButton = useCallback(({
    setOpen,
    value: buttonValue,
    inputProps,
    error,
    inputRef,
    InputProps,
    ...buttonParams
  }) => (
    <Button
      {...buttonParams}
      variant="outlined"
      id="invoice-offers-date-picker-button"
      sx={{
        fontSize:
        { xs: 10, md: 12 },
        width: '100%',
        borderRadius: 2,
        pl: 1,
        borderColor: 'gray.A400' }}
      onClick={() => setOpen((prev) => !prev)}
      startIcon={(
        <EventOutlinedIcon
          className={SECONDARY_ICON_CLASS_NAME}
          sx={{ width: { xs: 15, md: 25 }, height: { xs: 15, md: 25 } }}
        />
)}
    >
      <Typography color={buttonValue ? 'text.primary' : 'text.secondary'}>
        {buttonValue ? buttonValue.format('DD-MM-YYYY') : 'dd-mm-aaaa'}
      </Typography>
    </Button>
  ), []);

  return (
    <GridEditDateCell
      {...params}
      debtorAveragePaymentDays={debtorAveragePaymentDays}
      value={dateValue}
      onChange={onChange}
      minDate={UNTIL_FIFTEEN_DAYS}
      maxDate={maxDate}
      ButtonComponent={GridDateButton}
      useOnboardingDatePicker
    />
  );
};

DateToPayEditCellOnboarding.propTypes = {
  params: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    row: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      dateExpiration: PropTypes.objectOf(dayjs),
      dateIssued: PropTypes.objectOf(dayjs),
      offer: PropTypes.shape({
        fundingDaysAfterExpiration: PropTypes.number,
      }),
      receiver: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        invoicesActualPaymentTerm: PropTypes.number,
      }),
    }),
  }).isRequired,
  onDateChange: PropTypes.func,
};

DateToPayEditCellOnboarding.defaultProps = {
  onDateChange: () => {},
};

export default DateToPayEditCellOnboarding;
